import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { getAuth } from "firebase/auth";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useBalance } from '../context/BalanceContext';
import { PublicKey, Transaction } from "@solana/web3.js";
import { getAssociatedTokenAddress, createTransferInstruction } from "@solana/spl-token";
import { useDropzone } from "react-dropzone";
import { Buffer } from "buffer";
import { useNavigate } from "react-router-dom";

if (typeof window !== "undefined" && !window.Buffer) {
  window.Buffer = Buffer;
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const UploadForm = () => {
  // Common states
  const [title, setTitle] = useState("");
  const [error, setError] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { publicKey, signTransaction } = useWallet();
  const auth = getAuth();
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const navigate = useNavigate();

  // Tab state for switching between "pdf" and "thumbnail"
  const [selectedTab, setSelectedTab] = useState("pdf");

  // PDF state variables
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [validPDF, setValidPDF] = useState(false);
  const [pdfError, setPdfError] = useState("");

  // Thumbnail state variables (required)
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [validThumbnail, setValidThumbnail] = useState(false);
  const [thumbnailError, setThumbnailError] = useState("");

  const { tokenBalance } = useBalance();
  const [insufficientBalanceModalOpen, setInsufficientBalanceModalOpen] = useState(false);
  // PDF dropzone (max 1GB)
  const onPdfDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      // Set error message if the file is too large or any other error occurs
      const rejectionError = fileRejections[0].errors[0];
      const errorMessage =
        rejectionError.code === "file-too-large"
          ? "The file is too large. Only PDFs up to 1GB can be uploaded."
          : "Invalid PDF file. Only PDF files are allowed.";
      setValidPDF(false);
      setPdfError(errorMessage);
      setPdfFile(null);
      setPdfPreview(null);
      return;
    }
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.type !== "application/pdf") {
        setValidPDF(false);
        setPdfError("Only PDF files can be uploaded.");
        return;
      }
      setPdfFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPdfPreview(previewUrl);
      setValidPDF(true);
      setPdfError("");
    }
  }, []);

  const {
    getRootProps: getPdfRootProps,
    getInputProps: getPdfInputProps,
    isDragActive: isPdfDragActive,
  } = useDropzone({
    onDrop: onPdfDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    maxSize: 10 ** 9, // 1GB
  });

  // Thumbnail dropzone (jpg, jpeg, png; max 1MB)
  const onThumbnailDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      const rejectionError = fileRejections[0].errors[0];
      const errorMessage =
        rejectionError.code === "file-too-large"
          ? "The file is too large. Thumbnails can be up to 1MB."
          : "Invalid image file. Only jpg, jpeg, or png images are allowed.";
      setValidThumbnail(false);
      setThumbnailError(errorMessage);
      setThumbnailFile(null);
      setThumbnailPreview(null);
      return;
    }
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        setValidThumbnail(false);
        setThumbnailError("Only jpg, jpeg, or png image files are allowed.");
        return;
      }
      setThumbnailFile(file);
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
      setValidThumbnail(true);
      setThumbnailError("");
    }
  }, []);

  const {
    getRootProps: getThumbnailRootProps,
    getInputProps: getThumbnailInputProps,
    isDragActive: isThumbnailDragActive,
  } = useDropzone({
    onDrop: onThumbnailDrop,
    accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] },
    multiple: false,
    maxSize: 10 ** 6, // 1MB
  });

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Form submit handler (opens confirmation modal)
  const handleSubmit = (e) => {
    e.preventDefault();
    if (tokenBalance < 50) {
      setInsufficientBalanceModalOpen(true);
      return;
    }
    // Title, PDF, and thumbnail are required before submission
    if (title && validPDF && validThumbnail) {
      setConfirmationOpen(true);
    } else {
      setError("Title, PDF, and Thumbnail are all required.");
      setOpenSnackbar(true);
    }
  };

  // Cancel submission modal
  const handleCancelSubmit = () => {
    setConfirmationOpen(false);
  };

  // Snackbar close handler
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  // Paper submission process (uploads both PDF and thumbnail)
  const handleConfirmSubmit = async () => {
    setConfirmationOpen(false);
    try {
      if (!publicKey) {
        throw new Error("Wallet not connected");
      }

      setProcessing(true);
      setProcessingMessage("Creating transaction...");

      // 1. Create a transaction (e.g., token transfer)
      const receiverAddress = "DbXmYN4bdAJKitknozxGww3YEVaegeRhWKkoYLzomJND";
      const mintAddress = "Gc3eJ7fhAaJR2YsxyNs9nCb4HydBiLryKGxghW613sYK";
      const amount = 1;
      const senderTokenAddress = await getAssociatedTokenAddress(
        new PublicKey(mintAddress),
        publicKey
      );
      const receiverTokenAddress = await getAssociatedTokenAddress(
        new PublicKey(mintAddress),
        new PublicKey(receiverAddress)
      );
      const blockhashResponse = await fetch("/recent_blockhash");
      if (!blockhashResponse.ok) {
        throw new Error("Failed to fetch recent blockhash from proxy");
      }
      const blockhashData = await blockhashResponse.json();
      if (!blockhashData || !blockhashData.result || !blockhashData.result.value) {
        throw new Error("Invalid blockhash response structure");
      }
      const recentBlockhash = blockhashData.result.value.blockhash;
      const transaction = new Transaction().add(
        createTransferInstruction(
          senderTokenAddress,
          receiverTokenAddress,
          publicKey,
          amount * 10 ** 9
        )
      );
      transaction.recentBlockhash = recentBlockhash;
      transaction.feePayer = publicKey;
      const signedTx = await signTransaction(transaction);
      const serializedTx = signedTx.serialize().toString("base64");

      // 2. Send the transaction and metadata to the server to get signed upload URLs
      setProcessingMessage("Sending data to server...");
      const formData = new FormData();
      formData.append("transaction", serializedTx);
      formData.append("title", title);
      formData.append("user_id", auth.currentUser ? auth.currentUser.uid : publicKey.toBase58());
      formData.append("collection_name", "papers")
      const idToken = await auth.currentUser.getIdToken();
      const submitResponse = await fetch("/submit", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${idToken}`,
        },
        body: formData,
      });
      const submitResult = await submitResponse.json();
      if (!submitResponse.ok) {
        throw new Error(submitResult.error);
      }

      // 3. Use the signed upload URLs returned from the server to upload the PDF and thumbnail
      setProcessingMessage("Uploading PDF...");
      const { uploadSignedURL, uploadSignedURLThumbnail } = submitResult;

      // Upload PDF
      const uploadResponse = await fetch(uploadSignedURL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/pdf",
        },
        body: pdfFile,
      });
      if (!uploadResponse.ok) {
        throw new Error("Failed to upload PDF file");
      }

      // Upload Thumbnail
      if (thumbnailFile && uploadSignedURLThumbnail) {
        setProcessingMessage("Uploading thumbnail image...");
        const thumbnailUploadResponse = await fetch(uploadSignedURLThumbnail, {
          method: "PUT",
          headers: {
            "Content-Type": thumbnailFile.type,
          },
          body: thumbnailFile,
        });
        console.log(uploadSignedURLThumbnail)
        if (!thumbnailUploadResponse.ok) {
          throw new Error("Failed to upload thumbnail file");
        }
      }

      // Completion message and redirect
      setProcessingMessage("Upload complete! Redirecting...");
      setTimeout(() => {
        navigate("/", { state: { showCompleteModal: true } });
      }, 1000);
    } catch (err) {
      console.error("Error during submission:", err);
      setError("Submission error: " + err.message);
      setOpenSnackbar(true);
      setProcessing(false);
      setProcessingMessage("");
    }
  };

  const closeInsufficientBalanceModal = () => {
    setInsufficientBalanceModalOpen(false);
  };

  return (
    <Container
      maxWidth="sm"
      style={{
        maxHeight: "100vh",
        overflowY: "auto",
        background: "linear-gradient(135deg, #121212 0%, #1a1a2e 100%)",
        padding: "20px",
        borderRadius: "20px",
        color: "white",
        textAlign: "center",
        border: "1px solid rgba(0, 255, 255, 0.3)",
        boxShadow: "0px 0px 10px rgba(0, 255, 255, 0.5)",
        position: "relative",
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
        Submit Paper
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Title"
          variant="outlined"
          margin="dense"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          InputLabelProps={{ style: { color: "#bbb" } }}
          style={{ backgroundColor: "#1e1e1e", borderRadius: "10px", "& .MuiTabs-indicator": { backgroundColor: "#00ffcc" } }}
        />

        {/* Tabs for switching between PDF and Thumbnail — styled with green accents */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            marginTop: "20px",
            marginBottom: "10px",
            "& .MuiTabs-indicator": { backgroundColor: "#00ffcc" }
          }}
        >
          <Tab label="PDF" value="pdf" sx={{
            color: "#bbbbbb",
            "&.Mui-selected": { color: "#00ffcc" }
          }} />
          <Tab label="Thumbnail" value="thumbnail" sx={{
            color: "#bbbbbb",
            "&.Mui-selected": { color: "#00ffcc" }
          }} />
        </Tabs>

        {/* Display the upload area based on the selected tab */}
        {selectedTab === "pdf" ? (
          <div
            {...getPdfRootProps()}
            style={{
              border: "2px dashed #00ffcc",
              borderRadius: "10px",
              padding: "20px",
              textAlign: "center",
              marginTop: "15px",
              backgroundColor: isPdfDragActive ? "#1a1a2e" : "#222",
              color: "#fff",
              position: "relative",
              width: "100%",
              aspectRatio: "3 / 2", // Specify aspect ratio
            }}
          >
            <input {...getPdfInputProps()} />
            {validPDF && pdfPreview ? (
              <motion.div
                key="pdfPreview"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  overflow: "hidden",
                  height: "100%",
                }}
              >
                <iframe
                  src={pdfPreview}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    pointerEvents: "none",
                  }}
                  title="PDF Preview"
                />
              </motion.div>
            ) : (
              <div>
                {isPdfDragActive ? (
                  <Typography>Drop the PDF file here...</Typography>
                ) : (
                  <Typography>
                    Drag and drop a PDF file or click to select one
                  </Typography>
                )}
                {pdfError && (
                  <Typography variant="body2" style={{ color: "red", marginTop: "10px" }}>
                    {pdfError}
                  </Typography>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            {...getThumbnailRootProps()}
            style={{
              border: "2px dashed #00ffcc",
              borderRadius: "10px",
              padding: "20px",
              textAlign: "center",
              marginTop: "15px",
              backgroundColor: isThumbnailDragActive ? "#1a1a2e" : "#222",
              color: "#fff",
              position: "relative",
              width: "100%",
              aspectRatio: "3 / 2",
            }}
          >
            <input {...getThumbnailInputProps()} />
            {validThumbnail && thumbnailPreview ? (
              <motion.div
                key="thumbnailPreview"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  width: "100%",
                  borderRadius: "10px",
                  overflow: "hidden",
                  height: "100%",
                }}
              >
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail Preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </motion.div>
            ) : (
              <div>
                {isThumbnailDragActive ? (
                  <Typography>Drop the image file here...</Typography>
                ) : (
                  <Typography>
                    Drag and drop a thumbnail image or click to select one
                  </Typography>
                )}
                {thumbnailError && (
                  <Typography variant="body2" style={{ color: "red", marginTop: "10px" }}>
                    {thumbnailError}
                  </Typography>
                )}
              </div>
            )}
          </div>
        )}

        <Typography variant="body2" style={{ marginTop: "15px", color: "#ccc" }}>
          Your Token: <span style={{ fontWeight: "bold" }}>{tokenBalance} EMCII</span>
        </Typography>
        <Typography variant="body2" style={{ marginTop: "5px", color: "#ccc" }}>
          Submission Fee: <span style={{ fontWeight: "bold" }}>1.00 EMCII</span>
        </Typography>

        <Button
          type="submit"
          variant="contained"
          disabled={!(title && validPDF && validThumbnail)}
          style={{
            marginTop: "15px",
            backgroundColor: !(title && validPDF && validThumbnail) ? "#555" : "#00ffcc",
            color: "black",
            borderRadius: "10px",
          }}
        >
          Submit
        </Button>
      </form>

      {/* Confirmation Modal */}
      <Dialog open={confirmationOpen} onClose={handleCancelSubmit}>
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Once you submit, you will not be able to edit your paper.
            <br />
            Also, if the PDF or thumbnail becomes unavailable for a period of time,
            the paper will be automatically deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubmit}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Processing Overlay */}
      <AnimatePresence>
        {processing && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={overlayVariants}
            transition={{ duration: 0.3 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.8)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress style={{ color: "#00ffcc" }} />
            <Typography variant="h6" style={{ marginTop: "15px", color: "#fff" }}>
              {processingMessage}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 50, opacity: 0 }}
        >
          <Alert onClose={handleSnackbarClose} severity="error" variant="filled">
            {error}
          </Alert>
        </motion.div>
      </Snackbar>


      {/* 残高不足モーダル */}
      <Dialog open={insufficientBalanceModalOpen} onClose={closeInsufficientBalanceModal} fullWidth maxWidth="sm">
        <DialogTitle>Insufficient Balance</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            You need at least <strong>50 EMCII</strong> to evaluate this paper. Your current balance is {tokenBalance} EMCII.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button  onClose={closeInsufficientBalanceModal} variant="contained" sx={{ backgroundColor: '#444', color: '#fff' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UploadForm;
